import {
  SideNavigationV2Props,
  type SidebarComponent,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { LEFT_SIDE_NAV_WIDTH } from "skedyul-ui/ui";
import { SideNavigationComponentProps } from "../sideNavigation.schema";

import { open as openDialog } from "@store/features/layout/dialogSlice";
import { refreshMyWorkspaceNav } from "@store/features/account/workplaceSlice";
import { syncPagePresentationLocally } from "@store/features/crm/pageV2Slice";
import { store } from "@store/index";
import { iconTypes } from "skedyul-ui/src/components/ui/Icon";
import {
  buildCombinedSidebarEditorTree,
  collectNavigationPagesFromTrees,
  detectCrossBoundaryMoveIntent,
  findEditorItemById,
  getNavigationItemScope,
  isDropTargetInPersonalScope,
  splitLayoutChangesByScope,
  type SidebarNavigationEditorItem,
} from "@/lib/sidebarNavigationTree";
import { buildWorkplaceNavigationSectionsFromEditorTree } from "@/lib/buildWorkplaceNavigationSections";
import type { NavigationEditorItem } from "skedyul-ui/src/components/ui/RestructurableNavigationLayout";
import type { SidebarSectionProps } from "skedyul-ui/src/components/ui/SideNavigationV2/SideBar/navigation";
import { excludeArchivedWorkplaces } from "@/utils/excludeArchivedWorkplaces";
import { sortWorkplacesByName } from "@/utils/sortWorkplacesByName";
import { navigateToWorkplace } from "@/utils/workplaces";
import { openCreateWorkspacePageDialog } from "@schemas/pages/helpers/openCreateWorkspacePageDialog";
import { trpc } from "@utils/api";
import { toast } from "sonner";
import {
  getNewThreadHref,
  NEW_THREAD_SHORTCUT_KEYS,
} from "@/lib/threadNavigation";
import type { WorkspaceItem } from "skedyul-ui/src/components/ui/WorkspaceMenu";
import type { NavigationItem } from "skedyul-core/features/workplaces/features/navigationItems/resources/types";

const collectExistingNavigationPages = (
  myWorkspaceNavigationItems: NavigationItem[],
  teamNavigationItems: NavigationItem[],
) =>
  collectNavigationPagesFromTrees([
    myWorkspaceNavigationItems,
    teamNavigationItems,
  ]);

// Helper functions to check if a path is active
const isExactPathMatch = (currentPath: string, itemPath: string): boolean => {
  return currentPath === itemPath;
};

const isPathIncludes = (currentPath: string, itemPath: string): boolean => {
  return currentPath.includes(itemPath) && itemPath !== "";
};

export const sideNavigationListSchema = (
  args: SideNavigationComponentProps<SidebarComponent, { subdomain: string }>,
): SideNavigationV2Props<SidebarComponent> => {
  // get the params from the router context
  const subdomain = args.routerContext.params.subdomain;
  const pathname = args.routerContext.pathname;
  const state = args.state;
  const themeProvider = args.themeContext;
  const router = args.routerContext.router;

  // Helper functions to check if a path is active with subdomain context
  const isExactActive = (path: string): boolean => {
    const fullPath = `/${subdomain}${path}`;
    return isExactPathMatch(pathname, fullPath);
  };

  const isIncludesActive = (path: string): boolean => {
    const fullPath = `/${subdomain}${path}`;
    return isPathIncludes(pathname, fullPath);
  };

  const isDeveloperRoute = isIncludesActive("/developers");

  const workplaceData = state.workplace.workplace.data;
  const myWorkspaceNavigationItems =
    workplaceData?.myWorkspaceNavigationItems ?? [];
  const teamNavigationItems = workplaceData?.teamNavigationItems ?? [];
  const existingNavigationPages = collectExistingNavigationPages(
    myWorkspaceNavigationItems,
    teamNavigationItems,
  );
  const workplaceNavigationTree = buildCombinedSidebarEditorTree(
    myWorkspaceNavigationItems,
    teamNavigationItems,
  ) as NavigationEditorItem[];

  const refreshSidebarNav = async () => {
    await store.dispatch(refreshMyWorkspaceNav());
  };

  const handleNavigationReorder = async (
    changes: Array<{ id: string; parentId: string | null; sortIndex: number }>,
    navTree: NavigationEditorItem[],
  ) => {
    try {
      const { personal, team } = splitLayoutChangesByScope(changes, navTree);

      if (personal.length) {
        const result = await trpc.workplaces.reorderMyWorkspaceNav.mutate({
          items: personal,
        });
        if (!result.success) {
          return;
        }
      }

      if (team.length) {
        await trpc.workplaces.reorderTeamNavigation.mutate({
          items: team,
        });
      }

      await refreshSidebarNav();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update navigation",
      );
    }
  };

  const handleNavigationBeforeMove = async (input: {
    dragId: string;
    targetParentId: string | null;
    targetIndex: number;
    navTree: NavigationEditorItem[];
  }): Promise<boolean> => {
    const intent = detectCrossBoundaryMoveIntent(
      input.navTree,
      input.dragId,
      input.targetParentId,
      input.targetIndex,
    );

    if (!intent) {
      const dragged = findEditorItemById(input.navTree, input.dragId);
      if (dragged && input.targetParentId) {
        const targetInPersonal = isDropTargetInPersonalScope(
          input.navTree,
          input.targetParentId,
        );
        const sourceInPersonal = dragged.navScope === "personal";
        if (sourceInPersonal !== targetInPersonal) {
          toast.error(
            sourceInPersonal
              ? "Only pages can be moved into team navigation"
              : "That item cannot be moved into My Workspace",
          );
          return false;
        }
      }
      return true;
    }

    if (intent.type === "pin_team_to_personal") {
      try {
        await trpc.workplaces.pinTeamPageToMyWorkspace.mutate({
          teamNavItemId: intent.teamNavItemId,
          sortIndex: intent.sortIndex,
        });
        await refreshSidebarNav();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to pin page",
        );
      }
      return false;
    }

    if (intent.type === "move_personal_to_team") {
      try {
        await trpc.workplaces.transferMemberNavItemToTeamNav.mutate({
          memberNavItemId: intent.memberNavItemId,
          targetParentId: intent.targetParentId,
          sortIndex: intent.sortIndex,
        });
        await refreshSidebarNav();
        toast.success("Moved to team navigation");
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to move page",
        );
      }
      return false;
    }

    const dragged = findEditorItemById(input.navTree, input.dragId);
    store.dispatch(
      openDialog({
        schema: "MAKE_PAGE_TEAM_AVAILABLE",
        params: {
          memberNavItemId: intent.memberNavItemId,
          targetParentId: intent.targetParentId,
          sortIndex: intent.sortIndex,
          pageTitle: dragged?.label,
        },
      }),
    );
    return false;
  };

  const handleNavigationNestCreate = async (
    anchorId: string,
    dragId: string,
    navTree: NavigationEditorItem[],
  ) => {
    try {
      const scope = getNavigationItemScope(navTree, anchorId);
      const result =
        scope === "team"
          ? await trpc.workplaces.nestTeamNavigationItems.mutate({
              anchorId,
              dragId,
            })
          : await trpc.workplaces.nestMyWorkspaceNavItems.mutate({
              anchorId,
              dragId,
            });

      if (result.success) {
        await refreshSidebarNav();
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to nest navigation item",
      );
      throw error;
    }
  };

  const handleNavigationItemUpdate = async (
    input: {
      id: string;
      label: string;
      icon: iconTypes | null;
    },
    navTree: NavigationEditorItem[],
  ) => {
    const scope = getNavigationItemScope(navTree, input.id);
    try {
      const result =
        scope === "team"
          ? await trpc.workplaces.updateTeamNavItem.mutate({
              id: input.id,
              label: input.label,
              icon: input.icon,
            })
          : await trpc.workplaces.updateMyWorkspaceNavItem.mutate({
              id: input.id,
              label: input.label,
              icon: input.icon,
            });

      if (result.success) {
        await refreshSidebarNav();

        if (
          "pageId" in result &&
          result.pageId &&
          store.getState().pageV2.data?.id === result.pageId
        ) {
          store.dispatch(
            syncPagePresentationLocally({
              title: input.label.trim(),
              icon: input.icon,
            }),
          );
        }

        toast.success("Navigation updated");
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update navigation item",
      );
      throw error;
    }
  };

  const handleNavigationItemDelete = async (
    id: string,
    navTree: NavigationEditorItem[],
  ) => {
    const scope = getNavigationItemScope(navTree, id);
    try {
      if (scope === "team") {
        await trpc.workplaces.removeTeamNavItem.mutate({ id });
      } else {
        await trpc.workplaces.removeMyWorkspaceNavItem.mutate({ id });
      }
      await refreshSidebarNav();
      toast.success("Navigation item deleted");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to delete navigation item",
      );
      throw error;
    }
  };

  const handleAddPageToWorkspaceSearch = async (query: string) => {
    const results = await trpc.crm.searchAccessiblePages.query({
      query,
      limit: 40,
    });

    return results
      .filter(
        (page) =>
          !existingNavigationPages.pageIds.has(page.id) &&
          !existingNavigationPages.pagePaths.has(page.path),
      )
      .slice(0, 20);
  };

  const handleAddPageToWorkspaceSelect = async (page: {
    id: string;
    title: string;
    path: string;
  }) => {
    try {
      await trpc.workplaces.addPageToMyWorkspace.mutate({ pageId: page.id });
      await refreshSidebarNav();

      toast.success(`Added "${page.title}" to My Workspace`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add page",
      );
    }
  };

  const handleAddTeamSection = async (label: string) => {
    try {
      await trpc.workplaces.addTeamSection.mutate({ label });
      await refreshSidebarNav();

      toast.success(`Added team section "${label}"`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add section",
      );
    }
  };

  const handleCreateNewWorkspacePage = () => {
    void openCreateWorkspacePageDialog({ subdomain });
  };

  const handleExportTeamWorkspace = async () => {
    try {
      const result = await trpc.workplaces.exportTeamWorkspace.query();
      const blob = new Blob([JSON.stringify(result.schema, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("Team workspace exported");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to export workspace",
      );
    }
  };

  const handleImportTeamWorkspace = () => {
    store.dispatch(
      openDialog({
        schema: "IMPORT_TEAM_WORKSPACE",
        params: {},
      }),
    );
  };

  const handleAddPageUnderNavItem = async (
    anchorNavItemId: string,
    page: {
      id: string;
      title: string;
      path: string;
      visibility?: string;
    },
  ) => {
    const navTree = getNavTree();
    const scope = getNavigationItemScope(navTree, anchorNavItemId);

    try {
      if (scope === "team") {
        if (page.visibility === "private") {
          toast.error(
            "Only team-visible pages can be added to team navigation. Share the page with the team first.",
          );
          return;
        }
        await trpc.workplaces.addPageToTeamNav.mutate({
          pageId: page.id,
          parentId: anchorNavItemId,
        });
      } else {
        const memberNavItem = await trpc.workplaces.addPageToMyWorkspace.mutate(
          { pageId: page.id },
        );
        if (memberNavItem.id !== anchorNavItemId) {
          await trpc.workplaces.nestMyWorkspaceNavItems.mutate({
            anchorId: anchorNavItemId,
            dragId: memberNavItem.id,
          });
        }
      }

      await refreshSidebarNav();
      toast.success(`Added "${page.title}"`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add page",
      );
    }
  };

  const handleSearchPagesUnderNavItem = async (
    anchorNavItemId: string,
    query: string,
  ) => {
    const scope = getNavigationItemScope(getNavTree(), anchorNavItemId);
    const results = await handleAddPageToWorkspaceSearch(query);
    if (scope !== "team") {
      return results;
    }
    return results.filter((page) => page.visibility !== "private");
  };

  const navItemMenu = {
    pageTypeahead: {
      title: "Add page",
      placeholder: "Search pages...",
      emptyMessage: "No pages found",
      onSearch: handleAddPageToWorkspaceSearch,
      onSelect: handleAddPageToWorkspaceSelect,
      onCreateNewPage: handleCreateNewWorkspacePage,
      createNewPageLabel: "Create New Page",
    },
    onSearchPagesUnderItem: handleSearchPagesUnderNavItem,
    onAddPageUnderItem: handleAddPageUnderNavItem,
  };

  const myWorkspaceAddPageAction = {
    label: "Add to workspace",
    icon: "Plus" as iconTypes,
    menu: {
      pageTypeahead: {
        title: "Add page",
        placeholder: "Search pages...",
        emptyMessage: "No pages found",
        onSearch: handleAddPageToWorkspaceSearch,
        onSelect: handleAddPageToWorkspaceSelect,
        onCreateNewPage: handleCreateNewWorkspacePage,
        createNewPageLabel: "Create New Page",
      },
      addSection: {
        title: "Add section",
        placeholder: "Section name",
        onSubmit: handleAddTeamSection,
      },
      onExportWorkspace: handleExportTeamWorkspace,
      onImportWorkspace: handleImportTeamWorkspace,
    },
  };

  const withMyWorkspaceHeader = (
    sections: SidebarSectionProps[],
  ): SidebarSectionProps[] => {
    if (!sections.length) {
      return [
        {
          label: "My Workspace",
          action: myWorkspaceAddPageAction,
          groups: [{ items: [] }],
        },
      ];
    }

    return sections.map((section, index) => {
      if (index !== 0) return section;

      return {
        ...section,
        label: "My Workspace",
        isMyWorkspaceSection: true,
        action: myWorkspaceAddPageAction,
        navigation: section.navigation
          ? { ...section.navigation, draggable: false }
          : undefined,
      };
    });
  };

  const getNavTree = (): NavigationEditorItem[] =>
    buildCombinedSidebarEditorTree(
      store.getState().workplace.workplace.data?.myWorkspaceNavigationItems ??
        [],
      store.getState().workplace.workplace.data?.teamNavigationItems ?? [],
    );

  const getWorkplaceNavigationSections = (tree: NavigationEditorItem[]) =>
    withMyWorkspaceHeader(
      buildWorkplaceNavigationSectionsFromEditorTree(
        tree,
        subdomain,
        isIncludesActive,
      ),
    );

  // Build workspace items for the menu
  const currentWorkplaceData = state.workplace.workplace.data;
  const allWorkplaces = sortWorkplacesByName(
    excludeArchivedWorkplaces(state.workplace.workplaces.data),
  );

  const workspaceItems: WorkspaceItem[] = allWorkplaces.map((workplace) => ({
    id: workplace.id,
    name: workplace.name,
    avatar: {
      img: workplace.profileImageUrl || undefined,
      initials: workplace.name.charAt(0),
      name: workplace.name,
    },
    isCurrent: workplace.subdomain === subdomain,
  }));

  // Settings items based on route type
  const settingsItems = isDeveloperRoute
    ? [
        {
          label: "General",
          href: `/${subdomain}/developers/settings`,
          icon: "Settings2" as iconTypes,
          isActive: isIncludesActive("/developers/settings"),
        },
        {
          label: "Git",
          href: `/${subdomain}/developers/settings/git`,
          icon: "GitBranch" as iconTypes,
          isActive: isIncludesActive("/developers/settings/git"),
        },
      ]
    : [
        {
          label: "General",
          href: `/${subdomain}/settings`,
          icon: "Settings2" as iconTypes,
          isActive: isExactActive("/settings"),
        },
        {
          label: "Apps",
          href: `/${subdomain}/settings/apps`,
          icon: "Blocks" as iconTypes,
          isActive: isIncludesActive("/settings/apps"),
        },
        {
          label: "Models & Fields",
          href: `/${subdomain}/settings/crm`,
          icon: "Database" as iconTypes,
          isActive: isIncludesActive("/settings/crm"),
        },
        {
          label: "Workflows",
          href: `/${subdomain}/settings/workflows`,
          icon: "Workflow" as iconTypes,
          isActive: isIncludesActive("/settings/workflows"),
        },
      ];

  return {
    side: args.position,
    isOpen: state.sideNavigation[args.position].isOpen,
    width: LEFT_SIDE_NAV_WIDTH,
    component: {
      component: "Sidebar",
      props: {
        content: {
          sections: isDeveloperRoute
            ? [
                {
                  label: "",
                  groups: [
                    {
                      items: [
                        {
                          label: "Home",
                          href: `/${subdomain}`,
                          icon: "House",
                          isActive: isExactActive(""),
                        },
                      ],
                    },
                  ],
                },
                {
                  label: "Developers",
                  groups: [
                    {
                      collapsible: {
                        label: "Apps",
                        icon: "AppWindow" as iconTypes,
                      },
                      items: [
                        {
                          label: "Apps",
                          href: `/${subdomain}/developers/apps`,
                          icon: "AppWindow" as iconTypes,
                          isActive: isIncludesActive("/developers/apps"),
                        },
                        {
                          label: "Deployments",
                          href: `/${subdomain}/developers/deployments`,
                          icon: "Server" as iconTypes,
                          isActive: isIncludesActive("/developers/deployments"),
                        },
                        {
                          label: "Logs",
                          href: `/${subdomain}/developers/logs`,
                          icon: "Code" as iconTypes,
                          isActive: isIncludesActive("/developers/logs"),
                        },
                      ],
                    },
                    {
                      collapsible: {
                        label: "Agents",
                        icon: "Bot" as iconTypes,
                      },
                      items: [
                        {
                          label: "Agents",
                          href: `/${subdomain}/developers/agents`,
                          icon: "Bot" as iconTypes,
                          isActive: isIncludesActive("/developers/agents"),
                        },
                        {
                          label: "Skills",
                          href: `/${subdomain}/developers/skills`,
                          icon: "Sparkles" as iconTypes,
                          isActive: isIncludesActive("/developers/skills"),
                        },
                        {
                          label: "Playground",
                          href: `/${subdomain}/developers/playground`,
                          icon: "Play" as iconTypes,
                          isActive: isIncludesActive("/developers/playground"),
                        },
                      ],
                    },
                  ],
                },
              ]
            : [
                {
                  label: "",
                  groups: [
                    {
                      items: [
                        {
                          label: "Threads",
                          href: getNewThreadHref(subdomain),
                          icon: "MessagesSquare" as iconTypes,
                          isActive: isIncludesActive("/threads"),
                          shortcut: NEW_THREAD_SHORTCUT_KEYS,
                        },
                      ],
                    },
                  ],
                },
              ],
          ...(isDeveloperRoute
            ? {}
            : {
                workplaceNavigationDrag: {
                  tree: workplaceNavigationTree,
                  getSections: getWorkplaceNavigationSections,
                  onLayoutChange: (changes) => {
                    void handleNavigationReorder(changes, getNavTree());
                  },
                  onNestCreate: (anchorId, dragId) => {
                    void handleNavigationNestCreate(
                      anchorId,
                      dragId,
                      getNavTree(),
                    );
                  },
                  onBeforeMove: handleNavigationBeforeMove,
                },
                workplaceNavigationEdit: {
                  onUpdate: (input) => {
                    return handleNavigationItemUpdate(input, getNavTree());
                  },
                  onDelete: (id) => {
                    return handleNavigationItemDelete(id, getNavTree());
                  },
                  itemMenu: navItemMenu,
                },
              }),
        },
        workspaceMenu: {
          currentWorkspace: {
            name: currentWorkplaceData?.name || "Unknown",
            avatar: {
              img: currentWorkplaceData?.profileImageUrl || undefined,
              initials: currentWorkplaceData?.name?.charAt(0) || "W",
              name: currentWorkplaceData?.name || "Unknown",
            },
            href: isDeveloperRoute
              ? `/${subdomain}/developers/settings`
              : `/${subdomain}/settings`,
          },
          workplaces: workspaceItems,
          onWorkspaceSwitch: (workspace: WorkspaceItem) => {
            const selectedWorkplace = allWorkplaces.find(
              (w) => w.id === workspace.id,
            );
            if (selectedWorkplace?.subdomain) {
              void navigateToWorkplace(selectedWorkplace.subdomain, router);
            }
          },
          onCreateWorkspace: () => {
            store.dispatch(
              openDialog({
                schema: "NEW_WORKPLACE",
              }),
            );
          },
          settings: settingsItems,
          isDeveloperMode: isDeveloperRoute,
          theme: {
            current: (themeProvider?.theme as "light" | "dark") || "light",
            onToggle: () => {
              themeProvider?.setTheme(
                themeProvider?.theme === "dark" ? "light" : "dark",
              );
            },
          },
          onSignOut: () => {
            router.push("/sign-out");
          },
        },
      },
    },
  };
};
